import { useForm } from '@magicdoorinc/form';
import { createContext, createEffect, createMemo, createSignal, on, untrack, useContext } from 'solid-js';
import { produce, unwrap } from 'solid-js/store';
import { useCompanyBusinessInfo } from '~/contexts/company-business-info/BusinessInfoContext';
import { BusinessInfoInputLabel } from '~/pages/settings/company-settings/company-business/BusinessInfoInputLabel';
import { BusinessInfoRevisionStatus, PrincipalType } from '~/swagger/companies/Api';
import {
  getBusinessInfoFileValuesFromDocuments,
  getBusinessObligationConfigs,
  getEmptyBusinessInfoFileValues,
  getEmptyPrincipalFileValues,
  getPrincipalFormValuesFromDraft,
  mergeFormDocumentFieldsFromDraft,
} from '~/utils/companyBusinessDocuments';
import { hasCompanyBusinessInfoFormContent } from '~/utils/companyBusinessInfoPayload';
import { createPrincipalClientKey } from '~/utils/companyBusinessPrincipalClientKey';
import { isSupplementDocumentRequestDraft } from '~/utils/companyBusinessSupplement';
import { handleFormValidation } from '~/utils/formSideEffects';
import type { ParentProps } from 'solid-js';
import type { BusinessInfoDraftDto } from '~/swagger/companies/Api';
import type { PrincipalFormValue } from '~/utils/companyBusinessInfoPayload';

const EDITABLE_STATUSES: BusinessInfoRevisionStatus[] = [
  BusinessInfoRevisionStatus.Draft,
  BusinessInfoRevisionStatus.WaitingForInformation,
  BusinessInfoRevisionStatus.Rejected,
];

type CompanyBusinessInfoFormContextValue = {
  draft: () => BusinessInfoDraftDto | null | undefined;
  form: ReturnType<typeof useForm>;
  resolvedRequirement: () => BusinessInfoDraftDto['resolvedRequirement'];
  isEditable: () => boolean;
  draftMeta: () => {
    type?: BusinessInfoDraftDto['type'];
    status?: BusinessInfoDraftDto['status'];
    businessInfoId?: string;
    statusReason?: string;
  };
  formReady: () => boolean;
  addPrincipal: () => void;
  removePrincipal: (index: number) => void;
  isSupplementDocumentRequest: () => boolean;
  isFieldsEditLocked: () => boolean;
  unlockFieldsEdit: () => void;
};

const CompanyBusinessInfoFormContext = createContext<CompanyBusinessInfoFormContextValue>();

export const useCompanyBusinessInfoForm = () => {
  const context = useContext(CompanyBusinessInfoFormContext);
  if (!context) {
    throw new Error('CompanyBusinessInfoForm Context must be used within CompanyBusinessInfoFormProvider');
  }
  return context;
};

type CompanyBusinessInfoFormProviderProps = ParentProps<{
  disableSubmitWithoutChanges?: boolean | (() => void);
}>;

const CompanyBusinessInfoFormState = (props: CompanyBusinessInfoFormProviderProps) => {
  const { activeDraft } = useCompanyBusinessInfo();
  const [draftSource, setDraftSource] = createSignal<BusinessInfoDraftDto | null | undefined>(undefined);

  createEffect(() => {
    setDraftSource(activeDraft());
  });

  const draft = createMemo(() => draftSource());

  const form = useForm({
    disableSubmitWithoutChanges: untrack(() => props.disableSubmitWithoutChanges ?? handleFormValidation),
    inputLabel: BusinessInfoInputLabel,
  });

  const resolvedRequirement = createMemo(() => draft()?.resolvedRequirement);

  const isEditable = createMemo(() => {
    const status = draft()?.status;
    return status !== undefined && EDITABLE_STATUSES.includes(status);
  });

  const draftMeta = createMemo(() => ({
    type: draft()?.type,
    status: draft()?.status,
    businessInfoId: draft()?.businessInfoId,
    statusReason: draft()?.statusReason,
  }));

  const isSupplementDocumentRequest = createMemo(() => isSupplementDocumentRequestDraft(draft()));
  const [fieldsEditUnlocked, setFieldsEditUnlocked] = createSignal(false);
  const isFieldsEditLocked = createMemo(() => isSupplementDocumentRequest() && !fieldsEditUnlocked());

  createEffect(
    on(
      () => draft()?.businessInfoId,
      () => setFieldsEditUnlocked(false)
    )
  );

  const unlockFieldsEdit = () => setFieldsEditUnlocked(true);

  createEffect(
    on(
      () => draft()?.businessInfoId,
      (businessInfoId) => {
        const currentDraft = draft();
        const requirement = resolvedRequirement();

        if (!businessInfoId || !currentDraft) {
          if (!form.formStore.principals?.length) {
            form.setFormStore('principals', [
              {
                clientKey: createPrincipalClientKey(),
                type: PrincipalType.ControlPerson,
                driversLicense: { number: '', state: '' },
                principalFiles: getEmptyPrincipalFileValues(requirement),
              },
            ]);
          }
          if (requirement && !form.formStore.businessInfoFiles) {
            form.setFormStore('businessInfoFiles', getEmptyBusinessInfoFileValues(requirement));
          }
          return;
        }

        if (hasCompanyBusinessInfoFormContent(form.formStore)) {
          const merged = mergeFormDocumentFieldsFromDraft(form.formStore, currentDraft, requirement);
          form.setFormStore({
            ...form.formStore,
            legalName: form.formStore.legalName || currentDraft.legalName,
            principals: (form.formStore.principals ?? []).map((principal: PrincipalFormValue, index: number) => ({
              ...principal,
              clientKey: principal.clientKey ?? createPrincipalClientKey(currentDraft.principals[index]?.id),
              id: principal.id ?? currentDraft.principals[index]?.id,
              principalFiles: merged.principals?.[index]?.principalFiles ?? principal.principalFiles,
            })),
            businessInfoFiles:
              form.formStore.businessInfoFiles?.length || merged.businessInfoFiles?.length
                ? merged.businessInfoFiles
                : getBusinessInfoFileValuesFromDocuments(requirement, currentDraft.documents),
          });
          return;
        }

        const principals = getPrincipalFormValuesFromDraft(currentDraft.principals, requirement, currentDraft.documents);
        if (!principals.length) {
          principals.push({
            clientKey: createPrincipalClientKey(),
            type: PrincipalType.ControlPerson,
            driversLicense: { number: '', state: '' },
            principalFiles: getEmptyPrincipalFileValues(requirement),
          });
        } else if (principals[0]) {
          principals[0] = { ...principals[0], type: principals[0].type ?? PrincipalType.ControlPerson };
        }

        form.setFormStore({
          legalName: currentDraft.legalName,
          ein: currentDraft.ein,
          legalEntityType: currentDraft.legalEntityType,
          businessAddress: currentDraft.businessAddress,
          startDate: currentDraft.startDate,
          principals,
          businessInfoFiles: getBusinessInfoFileValuesFromDocuments(requirement, currentDraft.documents),
        });
      }
    )
  );

  createEffect(
    on(
      () => {
        const currentDraft = draft();
        if (!currentDraft?.businessInfoId) return undefined;
        const documentIds = currentDraft.documents
          .map((doc) => doc.id)
          .sort()
          .join('|');
        const principalIds = currentDraft.principals
          .map((principal) => principal.id)
          .sort()
          .join('|');
        return `${documentIds}:${principalIds}`;
      },
      (fileSyncKey, previousFileSyncKey) => {
        if (!fileSyncKey || !previousFileSyncKey || fileSyncKey === previousFileSyncKey) return;

        const currentDraft = draft();
        if (!currentDraft) return;

        const merged = mergeFormDocumentFieldsFromDraft(form.formStore, currentDraft, resolvedRequirement());
        const formPrincipals = form.formStore.principals ?? [];
        form.setFormStore({
          businessInfoFiles: merged.businessInfoFiles,
          principals: formPrincipals.map((principal: PrincipalFormValue, index: number) => ({
            ...principal,
            clientKey: principal.clientKey ?? createPrincipalClientKey(merged.principals?.[index]?.id),
            id: principal.id ?? merged.principals?.[index]?.id,
            principalFiles: merged.principals?.[index]?.principalFiles ?? principal.principalFiles,
          })),
        });
      }
    )
  );

  const formReady = createMemo(() => {
    const currentDraft = draft();
    const obligationCount = getBusinessObligationConfigs(resolvedRequirement()).length;

    if (!currentDraft?.businessInfoId) {
      return (form.formStore.principals?.length ?? 0) > 0;
    }

    if (!obligationCount) return true;

    return (form.formStore.businessInfoFiles?.length ?? 0) >= obligationCount;
  });

  const addPrincipal = () => {
    const requirement = resolvedRequirement();
    form.setFormStore(
      produce((draft) => {
        if (!draft.principals) {
          draft.principals = [];
        }
        draft.principals.push({
          clientKey: createPrincipalClientKey(),
          driversLicense: { number: '', state: '' },
          principalFiles: getEmptyPrincipalFileValues(requirement),
        });
      })
    );
  };

  const removePrincipal = (index: number) => {
    const principals = unwrap(form.formStore).principals;
    if (!principals || index < 0 || index >= principals.length) return;

    form.setFormStore(
      produce((draft) => {
        draft.principals?.splice(index, 1);
      })
    );
  };

  const value: CompanyBusinessInfoFormContextValue = {
    draft,
    form,
    resolvedRequirement,
    isEditable,
    draftMeta,
    formReady,
    addPrincipal,
    removePrincipal,
    isSupplementDocumentRequest,
    isFieldsEditLocked,
    unlockFieldsEdit,
  };

  return <CompanyBusinessInfoFormContext.Provider value={value}>{props.children}</CompanyBusinessInfoFormContext.Provider>;
};

export const CompanyBusinessInfoFormProvider = (props: CompanyBusinessInfoFormProviderProps) => (
  <CompanyBusinessInfoFormState disableSubmitWithoutChanges={props.disableSubmitWithoutChanges}>
    {props.children}
  </CompanyBusinessInfoFormState>
);
